import { api } from "./api";
import { getVapidPublicKey } from "./config";

const CLIENT_ID_KEY = "firewatch:client_id";
const ENABLED_KEY = "firewatch:push_enabled";

// --- IndexedDB mirror so the service worker (no localStorage access) can
// --- self-heal on `pushsubscriptionchange` (Samsung/FCM endpoint rotation).
function idbPut(data) {
  try {
    const req = indexedDB.open("firewatch-push", 1);
    req.onupgradeneeded = () => req.result.createObjectStore("kv");
    req.onsuccess = () => {
      const tx = req.result.transaction("kv", "readwrite");
      tx.objectStore("kv").put(data, "push-info");
    };
  } catch (e) {
    console.warn("push idb write failed", e);
  }
}

export function getOrCreateClientId() {
  let id = localStorage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
}

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPushEnabled() {
  return localStorage.getItem(ENABLED_KEY) === "1";
}

function setPushEnabled(v) {
  localStorage.setItem(ENABLED_KEY, v ? "1" : "0");
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    console.warn("SW registration failed", e);
    return null;
  }
}

export async function subscribeToPush(savedLocations) {
  if (!isPushSupported()) throw new Error("Push notifications not supported in this browser.");

  const perm = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied.");

  const reg = await navigator.serviceWorker.ready;

  // Pull the VAPID key from the cached app-config helper. First call in a
  // session triggers the /api/config fetch; every call after is a no-op
  // memoised promise. If the key is missing (misconfigured deployment)
  // we bail with a clear error rather than a cryptic base64 crash.
  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    throw new Error(
      "Push notifications are not configured on this deployment. " +
      "VAPID public key is missing from /api/config.",
    );
  }

  // Reuse existing subscription if present, else create a new one
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const subJson = sub.toJSON();
  const client_id = getOrCreateClientId();
  await api.post("/push/subscribe", {
    client_id,
    subscription: {
      endpoint: subJson.endpoint,
      keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
    },
    saved_locations: (savedLocations || []).map((l) => ({
      id: l.id,
      label: l.label,
      lat: l.lat,
      lon: l.lon,
      radius_mi: l.radius_mi,
    })),
  });

  setPushEnabled(true);
  idbPut({ client_id, vapidKey });
  return { client_id };
}

// Self-healing on app load: if the user previously enabled push, re-post the
// browser's CURRENT subscription to the server. Recovers from (a) FCM/Samsung
// endpoint rotation and (b) the server having pruned the record after a 410.
export async function resyncPush() {
  if (!getPushEnabled() || !isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }
    const subJson = sub.toJSON();
    const client_id = getOrCreateClientId();
    const subscription = {
      endpoint: subJson.endpoint,
      keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
    };
    const { data } = await api.post("/push/refresh-endpoint", {
      client_id,
      subscription,
    });
    // Self-heal: if the server record was resurrected after a prune (or lost
    // its saved locations), re-send the full subscription with this device's
    // locations — otherwise radius/proximity alerts silently never fire.
    if (data?.resurrected || data?.saved_locations_count === 0) {
      let locs = [];
      try {
        locs = JSON.parse(localStorage.getItem("okwatch:saved_locations:v1")) || [];
      } catch (err) {
        locs = [];
      }
      if (Array.isArray(locs) && locs.length > 0) {
        await api.post("/push/subscribe", {
          client_id,
          subscription,
          saved_locations: locs.map((l) => ({
            id: l.id,
            label: l.label,
            lat: l.lat,
            lon: l.lon,
            radius_mi: l.radius_mi,
          })),
        });
      }
    }
    const vapidKey = await getVapidPublicKey();
    idbPut({ client_id, vapidKey });
  } catch (e) {
    console.warn("Push resync failed", e);
  }
}

export async function syncSavedLocations(savedLocations) {
  if (!getPushEnabled() || !isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const subJson = sub.toJSON();
    const client_id = getOrCreateClientId();
    await api.post("/push/subscribe", {
      client_id,
      subscription: {
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
      },
      saved_locations: (savedLocations || []).map((l) => ({
        id: l.id,
        label: l.label,
        lat: l.lat,
        lon: l.lon,
        radius_mi: l.radius_mi,
      })),
    });
  } catch (e) {
    console.warn("Push sync failed", e);
  }
}

export async function unsubscribeFromPush() {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    } catch (e) {
      console.warn("Local unsubscribe failed", e);
    }
  }
  if (client_id) {
    try {
      await api.delete(`/push/subscribe/${client_id}`);
    } catch (e) {
      console.warn("Server unsubscribe failed", e);
    }
  }
  setPushEnabled(false);
}

export async function sendTestPush() {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if (!client_id) throw new Error("Not subscribed");
  await api.post(`/push/test/${client_id}`);
}

export async function getDailyBriefing() {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if (!client_id) return false;
  const { data } = await api.get(`/push/daily-briefing/${client_id}`);
  return !!data.enabled;
}

export async function setDailyBriefing(enabled) {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if (!client_id) throw new Error("Not subscribed");
  await api.post("/push/daily-briefing", { client_id, enabled });
  return enabled;
}

export async function getQuietHours() {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if (!client_id) return { enabled: false, start_min: 1320, end_min: 420 };
  const { data } = await api.get(`/push/quiet-hours/${client_id}`);
  return data;
}

export async function setQuietHours({ enabled, start_min, end_min }) {
  const client_id = localStorage.getItem(CLIENT_ID_KEY);
  if (!client_id) throw new Error("Not subscribed");
  await api.post("/push/quiet-hours", {
    client_id,
    enabled,
    start_min,
    end_min,
    tz_offset_min: new Date().getTimezoneOffset(),
  });
}
